import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Clock, Car, MapPin, Navigation, Loader2, Check, Zap, Phone, MessageCircle, ChevronRight, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const WhatsAppBookingFlow = ({ isOpen, onClose, phoneNumber = "+34613076099", lang = "es" }) => {
  const [step, setStep] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [technicianFound, setTechnicianFound] = useState(false);
  const [searchProgress, setSearchProgress] = useState(0);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  
  // Ref to store interval ID for cleanup
  const searchIntervalRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  const [formData, setFormData] = useState({
    timing: '', // 'now' or 'later'
    scheduledTime: '',
    carBrand: '',
    carModel: '',
    locationType: '',
    address: '',
    additionalInfo: ''
  });

  // Translations
  const translations = {
    es: {
      title: "Solicitar Arranque",
      subtitle: "Servicio express Barcelona",
      step1Title: "¿Cuándo lo necesitas?",
      step1Subtitle: "Selecciona el momento del servicio",
      now: "Ahora mismo",
      nowDesc: "Te atendemos hoy",
      later: "Programar",
      laterDesc: "Elige hora",
      whatTime: "¿A qué hora?",
      step2Title: "¿Qué coche tienes?",
      step2Subtitle: "Selecciona marca y modelo",
      brand: "Marca",
      modelOptional: "Modelo (opcional)",
      modelPlaceholder: "Ej: Golf, Clio, 308...",
      step3Title: "¿Dónde está el coche?",
      step3Subtitle: "Tipo de ubicación",
      step4Title: "¿Cuál es la dirección?",
      step4Subtitle: "Dirección exacta en Barcelona",
      addressLabel: "Dirección completa",
      addressPlaceholder: "Ej: Carrer de Balmes 123, Barcelona",
      addressHint: "Incluye calle, número y barrio",
      additionalInfo: "Información adicional (opcional)",
      additionalPlaceholder: "Ej: Coche rojo, planta -2 del parking...",
      totalPrice: "Precio total",
      dayRate: "Tarifa día",
      nightRate: "Tarifa nocturna/fin de semana",
      airportSupplement: "Incluye +25€ Aeropuerto El Prat",
      baseRate: "Tarifa base",
      travelIncluded: "Desplazamiento",
      included: "Incluido",
      noStartNoPay: "Si no arranca",
      noPay: "No pagas",
      searching: "Localizando técnicos...",
      zone: "Zona",
      scanning: "Escaneando zona de Barcelona...",
      contacting: "Contactando técnicos cercanos...",
      verifying: "Verificando disponibilidad...",
      confirming: "Confirmando asignación...",
      located: "¡Técnico localizado!",
      technicianAvailable: "¡Técnico disponible en tu zona!",
      technicianName: "ArrancaYa Express",
      technicianDesc: "Técnico certificado • Barcelona",
      ready: "LISTO",
      confirmWhatsApp: "Confirmar y Enviar por WhatsApp",
      preferCall: "¿Prefieres llamar?",
      back: "Atrás",
      continue: "Continuar",
      vatIncluded: "IVA incluido",
      noCommitment: "Sin compromiso",
      fixedPrice: "Precio fijo",
      payAfter: "Pago tras servicio",
      airportWarningTitle: "Suplemento Aeropuerto El Prat",
      airportWarningDesc: "+25€ debido al desplazamiento y acceso especial",
      locationStreet: "En la calle",
      locationParking: "Parking público",
      locationGarage: "Garaje privado",
      locationMall: "Centro comercial",
      locationAirport: "Aeropuerto El Prat",
      locationOther: "Otro lugar"
    },
    cat: {
      title: "Sol·licitar Arrencada",
      subtitle: "Servei express Barcelona",
      step1Title: "Quan ho necessites?",
      step1Subtitle: "Selecciona el moment del servei",
      now: "Ara mateix",
      nowDesc: "T'atenem avui",
      later: "Programar",
      laterDesc: "Tria hora",
      whatTime: "A quina hora?",
      step2Title: "Quin cotxe tens?",
      step2Subtitle: "Selecciona marca i model",
      brand: "Marca",
      modelOptional: "Model (opcional)",
      modelPlaceholder: "Ex: Golf, Clio, 308...",
      step3Title: "On és el cotxe?",
      step3Subtitle: "Tipus d'ubicació",
      step4Title: "Quina és l'adreça?",
      step4Subtitle: "Adreça exacta a Barcelona",
      addressLabel: "Adreça completa",
      addressPlaceholder: "Ex: Carrer de Balmes 123, Barcelona",
      addressHint: "Inclou carrer, número i barri",
      additionalInfo: "Informació addicional (opcional)",
      additionalPlaceholder: "Ex: Cotxe vermell, planta -2 del pàrquing...",
      totalPrice: "Preu total",
      dayRate: "Tarifa dia",
      nightRate: "Tarifa nocturna/cap de setmana",
      airportSupplement: "Inclou +25€ Aeroport El Prat",
      baseRate: "Tarifa base",
      travelIncluded: "Desplaçament",
      included: "Inclòs",
      noStartNoPay: "Si no arrenca",
      noPay: "No pagues",
      searching: "Localitzant tècnics...",
      zone: "Zona",
      scanning: "Escanejant zona de Barcelona...",
      contacting: "Contactant tècnics propers...",
      verifying: "Verificant disponibilitat...",
      confirming: "Confirmant assignació...",
      located: "Tècnic localitzat!",
      technicianAvailable: "Tècnic disponible a la teva zona!",
      technicianName: "ArrancaYa Express",
      technicianDesc: "Tècnic certificat • Barcelona",
      ready: "LLEST",
      confirmWhatsApp: "Confirmar i Enviar per WhatsApp",
      preferCall: "Prefereixes trucar?",
      back: "Enrere",
      continue: "Continuar",
      vatIncluded: "IVA inclòs",
      noCommitment: "Sense compromís",
      fixedPrice: "Preu fix",
      payAfter: "Pagament després del servei",
      airportWarningTitle: "Suplement Aeroport El Prat",
      airportWarningDesc: "+25€ pel desplaçament i accés especial",
      locationStreet: "Al carrer",
      locationParking: "Pàrquing públic",
      locationGarage: "Garatge privat",
      locationMall: "Centre comercial",
      locationAirport: "Aeroport El Prat",
      locationOther: "Altre lloc"
    },
    en: {
      title: "Request Jump-start",
      subtitle: "Express service Barcelona",
      step1Title: "When do you need it?",
      step1Subtitle: "Select the service time",
      now: "Right now",
      nowDesc: "We'll help you today",
      later: "Schedule",
      laterDesc: "Choose time",
      whatTime: "What time?",
      step2Title: "What car do you have?",
      step2Subtitle: "Select brand and model",
      brand: "Brand",
      modelOptional: "Model (optional)",
      modelPlaceholder: "E.g: Golf, Clio, 308...",
      step3Title: "Where is the car?",
      step3Subtitle: "Location type",
      step4Title: "What's the address?",
      step4Subtitle: "Exact address in Barcelona",
      addressLabel: "Full address",
      addressPlaceholder: "E.g: Carrer de Balmes 123, Barcelona",
      addressHint: "Include street, number and neighborhood",
      additionalInfo: "Additional information (optional)",
      additionalPlaceholder: "E.g: Red car, floor -2 of parking...",
      totalPrice: "Total price",
      dayRate: "Day rate",
      nightRate: "Night/weekend rate",
      airportSupplement: "Includes +€25 El Prat Airport",
      baseRate: "Base rate",
      travelIncluded: "Travel",
      included: "Included",
      noStartNoPay: "If it doesn't start",
      noPay: "You don't pay",
      searching: "Locating technicians...",
      zone: "Zone",
      scanning: "Scanning Barcelona area...",
      contacting: "Contacting nearby technicians...",
      verifying: "Verifying availability...",
      confirming: "Confirming assignment...",
      located: "Technician located!",
      technicianAvailable: "Technician available in your area!",
      technicianName: "ArrancaYa Express",
      technicianDesc: "Certified technician • Barcelona",
      ready: "READY",
      confirmWhatsApp: "Confirm and Send via WhatsApp",
      preferCall: "Prefer to call?",
      back: "Back",
      continue: "Continue",
      vatIncluded: "VAT included",
      noCommitment: "No commitment",
      fixedPrice: "Fixed price",
      payAfter: "Pay after service",
      airportWarningTitle: "El Prat Airport Supplement",
      airportWarningDesc: "+€25 due to travel and special access",
      locationStreet: "On the street",
      locationParking: "Public parking",
      locationGarage: "Private garage",
      locationMall: "Shopping center",
      locationAirport: "El Prat Airport",
      locationOther: "Other location"
    }
  };

  const t = translations[lang] || translations.es;

  // Car brands for autocomplete
  const carBrands = ['Seat', 'Volkswagen', 'Renault', 'Peugeot', 'Citroën', 'Opel', 'Ford', 'BMW', 'Mercedes', 'Audi', 'Toyota', 'Hyundai', 'Kia', 'Nissan', 'Fiat', 'Skoda', 'Dacia', 'Mazda', 'Honda', 'Volvo', 'Mini', 'Otro'];
  
  const locationTypes = [
    { id: 'street', label: t.locationStreet, icon: '🚗', supplement: 0 },
    { id: 'parking', label: t.locationParking, icon: '🅿️', supplement: 0 },
    { id: 'garage', label: t.locationGarage, icon: '🏠', supplement: 0 },
    { id: 'center', label: t.locationMall, icon: '🏬', supplement: 0 },
    { id: 'airport', label: t.locationAirport, icon: '✈️', supplement: 25 },
    { id: 'other', label: t.locationOther, icon: '📍', supplement: 0 }
  ];

  // Calculate price based on timing and location
  const calculatePrice = useCallback(() => {
    const now = new Date();
    const hour = now.getHours();
    const day = now.getDay();
    
    let basePrice = 69;
    let isNightRate = false;
    let isAirport = false;
    let airportSupplement = 0;
    
    // Check if airport location (check for the label containing airport keywords)
    const airportKeywords = ['Aeropuerto', 'Aeroport', 'Airport', 'El Prat'];
    if (airportKeywords.some(kw => formData.locationType.includes(kw))) {
      isAirport = true;
      airportSupplement = 25;
    }
    
    if (formData.timing === 'now') {
      // Night rate: 22:00 - 8:00
      if (hour >= 22 || hour < 8) {
        basePrice = 99;
        isNightRate = true;
      }
      // Weekend rate
      if (day === 0 || day === 6) {
        basePrice = 99;
        isNightRate = true;
      }
    } else if (formData.scheduledTime) {
      const [hours] = formData.scheduledTime.split(':').map(Number);
      if (hours >= 22 || hours < 8) {
        basePrice = 99;
        isNightRate = true;
      }
    }
    
    const totalPrice = basePrice + airportSupplement;
    
    return { price: totalPrice, basePrice, isNightRate, isAirport, airportSupplement };
  }, [formData.timing, formData.scheduledTime, formData.locationType]);

  // Simulate technician search - LONGER animation for realism
  const startTechnicianSearch = useCallback(() => {
    // Clear any existing interval first
    if (searchIntervalRef.current) {
      clearInterval(searchIntervalRef.current);
    }
    
    setIsSearching(true);
    setSearchProgress(0);
    setTechnicianFound(false);
    
    // Slower progress for more realistic feel (8-12 seconds total)
    searchIntervalRef.current = setInterval(() => {
      setSearchProgress(prev => {
        if (prev >= 100) {
          if (searchIntervalRef.current) {
            clearInterval(searchIntervalRef.current);
            searchIntervalRef.current = null;
          }
          setIsSearching(false);
          setTechnicianFound(true);
          return 100;
        }
        // Slower increments for longer animation
        return prev + Math.random() * 8 + 2;
      });
    }, 500); // Slower interval
  }, []);

  // Move to next step
  const nextStep = () => {
    if (step === 4) {
      // Calculate price before showing search
      const priceInfo = calculatePrice();
      setCalculatedPrice(priceInfo);
      setStep(5);
      // Start search animation after a brief delay
      searchTimeoutRef.current = setTimeout(() => {
        startTechnicianSearch();
      }, 1500);
    } else {
      setStep(prev => prev + 1);
    }
  };

  // Send to WhatsApp
  const sendToWhatsApp = () => {
    const timingText = formData.timing === 'now' 
      ? '🚨 URGENTE - Ahora mismo' 
      : `📅 Programado: ${formData.scheduledTime}`;
    
    let priceDetails = `💰 Precio: ${calculatedPrice?.price}€`;
    if (calculatedPrice?.isAirport) {
      priceDetails += ` (incluye +${calculatedPrice.airportSupplement}€ Aeropuerto El Prat)`;
    }
    
    const message = `¡Hola! Necesito servicio de arranque:

${timingText}

🚗 Vehículo: ${formData.carBrand} ${formData.carModel}
📍 Ubicación: ${formData.locationType}
🗺️ Dirección: ${formData.address}
${formData.additionalInfo ? `ℹ️ Info adicional: ${formData.additionalInfo}` : ''}

${priceDetails}`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber.replace(/\s/g, '')}?text=${encodedMessage}`, '_blank');
    
    // Track conversion with GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'whatsapp_conversion',
        'event_category': 'conversion',
        'event_label': 'booking_flow_complete',
        'value': calculatedPrice?.price,
        'price_base': calculatedPrice?.basePrice,
        'is_night_rate': calculatedPrice?.isNightRate,
        'is_airport': calculatedPrice?.isAirport,
        'airport_supplement': calculatedPrice?.airportSupplement || 0,
        'car_brand': formData.carBrand,
        'location_type': formData.locationType,
        'timing': formData.timing
      });
    }
    
    onClose();
  };

  // Cleanup function to clear all timers
  const clearAllTimers = useCallback(() => {
    if (searchIntervalRef.current) {
      clearInterval(searchIntervalRef.current);
      searchIntervalRef.current = null;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, []);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      // Clear all timers when modal closes
      clearAllTimers();
      
      setStep(1);
      setFormData({
        timing: '',
        scheduledTime: '',
        carBrand: '',
        carModel: '',
        locationType: '',
        address: '',
        additionalInfo: ''
      });
      setIsSearching(false);
      setTechnicianFound(false);
      setSearchProgress(0);
      setCalculatedPrice(null);
    }
  }, [isOpen, clearAllTimers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);

  if (!isOpen) return null;

  const canProceed = () => {
    switch (step) {
      case 1: return formData.timing !== '';
      case 2: return formData.carBrand !== '';
      case 3: return formData.locationType !== '';
      case 4: return formData.address.length >= 5;
      default: return true;
    }
  };

  // Check if selected location is airport
  const isAirportSelected = locationTypes.find(loc => loc.label === formData.locationType)?.id === 'airport';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">{t.title}</h2>
              <p className="text-green-100 text-sm">{t.subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-gray-200">
          <div 
            className="h-full bg-green-500 transition-all duration-500"
            style={{ width: `${(step / 6) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Timing */}
          {step === 1 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right duration-300">
              <div className="text-center mb-6">
                <Clock className="h-12 w-12 text-green-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900">{t.step1Title}</h3>
                <p className="text-gray-500 text-sm mt-1">{t.step1Subtitle}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setFormData(prev => ({ ...prev, timing: 'now' }))}
                  className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                    formData.timing === 'now'
                      ? 'border-green-500 bg-green-50 shadow-lg scale-105'
                      : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="text-center">
                    <span className="text-4xl mb-2 block">🚨</span>
                    <span className="font-bold text-gray-900 block">{t.now}</span>
                    <span className="text-xs text-gray-500">{t.nowDesc}</span>
                  </div>
                </button>
                
                <button
                  onClick={() => setFormData(prev => ({ ...prev, timing: 'later' }))}
                  className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                    formData.timing === 'later'
                      ? 'border-green-500 bg-green-50 shadow-lg scale-105'
                      : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="text-center">
                    <span className="text-4xl mb-2 block">📅</span>
                    <span className="font-bold text-gray-900 block">{t.later}</span>
                    <span className="text-xs text-gray-500">{t.laterDesc}</span>
                  </div>
                </button>
              </div>
              
              {formData.timing === 'later' && (
                <div className="animate-in fade-in slide-in-from-bottom duration-200">
                  <Label className="text-gray-700 font-medium">{t.whatTime}</Label>
                  <Input
                    type="time"
                    value={formData.scheduledTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, scheduledTime: e.target.value }))}
                    className="mt-2"
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 2: Car Info */}
          {step === 2 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right duration-300">
              <div className="text-center mb-6">
                <Car className="h-12 w-12 text-green-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900">{t.step2Title}</h3>
                <p className="text-gray-500 text-sm mt-1">{t.step2Subtitle}</p>
              </div>
              
              <div>
                <Label className="text-gray-700 font-medium">{t.brand}</Label>
                <div className="grid grid-cols-4 gap-2 mt-2 max-h-40 overflow-y-auto">
                  {carBrands.map((brand) => (
                    <button
                      key={brand}
                      onClick={() => setFormData(prev => ({ ...prev, carBrand: brand }))}
                      className={`p-2 text-sm rounded-lg border transition-all ${
                        formData.carBrand === brand
                          ? 'border-green-500 bg-green-50 text-green-700 font-medium'
                          : 'border-gray-200 hover:border-green-300 text-gray-700'
                      }`}
                    >
                      {brand}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-gray-700 font-medium">{t.modelOptional}</Label>
                <Input
                  placeholder={t.modelPlaceholder}
                  value={formData.carModel}
                  onChange={(e) => setFormData(prev => ({ ...prev, carModel: e.target.value }))}
                  className="mt-2"
                />
              </div>
            </div>
          )}

          {/* Step 3: Location Type */}
          {step === 3 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right duration-300">
              <div className="text-center mb-6">
                <MapPin className="h-12 w-12 text-green-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900">{t.step3Title}</h3>
                <p className="text-gray-500 text-sm mt-1">{t.step3Subtitle}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {locationTypes.map((loc) => (
                  <button
                    key={loc.id}
                    onClick={() => setFormData(prev => ({ ...prev, locationType: loc.label }))}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 text-left relative ${
                      formData.locationType === loc.label
                        ? 'border-green-500 bg-green-50 shadow-md'
                        : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-2xl block mb-1">{loc.icon}</span>
                    <span className="font-medium text-gray-900 text-sm">{loc.label}</span>
                    {loc.supplement > 0 && (
                      <span className="absolute top-2 right-2 bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-0.5 rounded">
                        +{loc.supplement}€
                      </span>
                    )}
                  </button>
                ))}
              </div>
              
              {isAirportSelected && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-yellow-800 font-medium">{t.airportWarningTitle}</p>
                    <p className="text-xs text-yellow-700">{t.airportWarningDesc}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Address */}
          {step === 4 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right duration-300">
              <div className="text-center mb-6">
                <Navigation className="h-12 w-12 text-green-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900">{t.step4Title}</h3>
                <p className="text-gray-500 text-sm mt-1">{t.step4Subtitle}</p>
              </div>
              
              <div>
                <Label className="text-gray-700 font-medium">{t.addressLabel}</Label>
                <Input
                  placeholder={t.addressPlaceholder}
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">{t.addressHint}</p>
              </div>
              
              <div>
                <Label className="text-gray-700 font-medium">{t.additionalInfo}</Label>
                <Input
                  placeholder={t.additionalPlaceholder}
                  value={formData.additionalInfo}
                  onChange={(e) => setFormData(prev => ({ ...prev, additionalInfo: e.target.value }))}
                  className="mt-2"
                />
              </div>
            </div>
          )}

          {/* Step 5: Searching & Price */}
          {step === 5 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right duration-300">
              {/* Price Card */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 text-white">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-gray-400 text-sm">{t.totalPrice}</p>
                    <div className="flex items-baseline">
                      <span className="text-5xl font-black">{calculatedPrice?.price}</span>
                      <span className="text-2xl ml-1">€</span>
                    </div>
                    <p className="text-green-400 text-sm mt-1">
                      📌 {calculatedPrice?.isNightRate ? t.nightRate : t.dayRate}
                    </p>
                    {calculatedPrice?.isAirport && (
                      <p className="text-yellow-400 text-xs mt-1">
                        ✈️ {t.airportSupplement}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
                      {t.vatIncluded}
                    </span>
                  </div>
                </div>
                
                <div className="border-t border-gray-700 pt-4 space-y-2 text-sm">
                  {calculatedPrice?.isAirport && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">{t.baseRate}</span>
                      <span className="text-white">{calculatedPrice.basePrice}€</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-400">{t.travelIncluded}</span>
                    <span className="text-green-400">{t.included}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">{t.noStartNoPay}</span>
                    <span className="text-green-400">{t.noPay}</span>
                  </div>
                </div>
              </div>

              {/* Live Map Search */}
              <div className="bg-gray-100 rounded-2xl p-6 relative overflow-hidden min-h-[200px]">
                {/* Fake map background with grid pattern */}
                <div className="absolute inset-0 opacity-30">
                  <div className="w-full h-full bg-gradient-to-br from-green-100 via-blue-50 to-green-100" />
                  {/* Fake streets grid */}
                  <div className="absolute top-1/5 left-0 right-0 h-px bg-gray-400" />
                  <div className="absolute top-2/5 left-0 right-0 h-px bg-gray-400" />
                  <div className="absolute top-3/5 left-0 right-0 h-px bg-gray-400" />
                  <div className="absolute top-4/5 left-0 right-0 h-px bg-gray-400" />
                  <div className="absolute left-1/5 top-0 bottom-0 w-px bg-gray-400" />
                  <div className="absolute left-2/5 top-0 bottom-0 w-px bg-gray-400" />
                  <div className="absolute left-3/5 top-0 bottom-0 w-px bg-gray-400" />
                  <div className="absolute left-4/5 top-0 bottom-0 w-px bg-gray-400" />
                </div>

                {/* Searching Animation */}
                {isSearching && (
                  <div className="relative z-10">
                    <div className="flex items-center justify-center mb-6">
                      <div className="relative">
                        <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-green-500/50">
                          <MapPin className="h-10 w-10 text-white" />
                        </div>
                        {/* Radar rings */}
                        <div className="absolute inset-0 rounded-full border-4 border-green-500 animate-ping opacity-75" />
                        <div className="absolute -inset-6 rounded-full border-2 border-green-400 animate-ping opacity-50" style={{ animationDelay: '0.3s' }} />
                        <div className="absolute -inset-12 rounded-full border border-green-300 animate-ping opacity-25" style={{ animationDelay: '0.6s' }} />
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <p className="font-bold text-gray-900 text-lg mb-2">{t.searching}</p>
                      <p className="text-sm text-gray-600 mb-4">
                        📍 {t.zone}: <strong>{formData.address.split(',')[0] || 'Barcelona'}</strong>
                      </p>
                      
                      {/* Progress bar */}
                      <div className="h-3 bg-gray-300 rounded-full overflow-hidden shadow-inner">
                        <div 
                          className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-500 ease-out"
                          style={{ width: `${Math.min(searchProgress, 100)}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-3 font-medium">
                        {searchProgress < 20 && `🔍 ${t.scanning}`}
                        {searchProgress >= 20 && searchProgress < 45 && `📡 ${t.contacting}`}
                        {searchProgress >= 45 && searchProgress < 70 && `🛵 ${t.verifying}`}
                        {searchProgress >= 70 && searchProgress < 90 && `✅ ${t.confirming}`}
                        {searchProgress >= 90 && `🎯 ${t.located}`}
                      </p>
                    </div>
                  </div>
                )}

                {/* Technician Found */}
                {technicianFound && (
                  <div className="relative z-10 animate-in fade-in zoom-in duration-500">
                    <div className="flex items-center justify-center mb-6">
                      <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/50">
                        <Check className="h-12 w-12 text-white" />
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <p className="font-bold text-green-700 text-xl mb-4">{t.technicianAvailable}</p>
                      
                      <div className="bg-white rounded-xl p-4 shadow-md border border-green-200">
                        <div className="flex items-center space-x-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-md">
                            <Zap className="h-7 w-7 text-white" />
                          </div>
                          <div className="text-left flex-1">
                            <p className="font-bold text-gray-900 text-lg">{t.technicianName}</p>
                            <p className="text-sm text-gray-500">{t.technicianDesc}</p>
                          </div>
                          <div>
                            <span className="bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-full animate-pulse">
                              {t.ready}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-6 flex space-x-3">
            {step > 1 && step < 5 && (
              <Button
                variant="outline"
                onClick={() => setStep(prev => prev - 1)}
                className="flex-1"
              >
                {t.back}
              </Button>
            )}
            
            {step < 5 && (
              <Button
                onClick={nextStep}
                disabled={!canProceed()}
                className={`flex-1 bg-green-600 hover:bg-green-700 text-white font-bold ${
                  !canProceed() ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {t.continue}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            
            {step === 5 && technicianFound && (
              <Button
                onClick={sendToWhatsApp}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-6 text-lg"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                {t.confirmWhatsApp}
              </Button>
            )}
          </div>
          
          {/* Alternative: Call directly */}
          {step === 5 && (
            <div className="mt-4 text-center">
              <p className="text-gray-500 text-sm mb-2">{t.preferCall}</p>
              <button
                onClick={() => window.location.href = `tel:${phoneNumber}`}
                className="inline-flex items-center text-green-600 font-bold hover:text-green-700"
              >
                <Phone className="h-4 w-4 mr-1" />
                613 076 099
              </button>
            </div>
          )}
        </div>

        {/* Footer - Trust badges */}
        <div className="bg-gray-50 px-6 py-3 flex items-center justify-center space-x-6 text-xs text-gray-500">
          <span className="flex items-center">
            <Check className="h-3 w-3 text-green-500 mr-1" />
            {t.noCommitment}
          </span>
          <span className="flex items-center">
            <Check className="h-3 w-3 text-green-500 mr-1" />
            {t.fixedPrice}
          </span>
          <span className="flex items-center">
            <Check className="h-3 w-3 text-green-500 mr-1" />
            {t.payAfter}
          </span>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppBookingFlow;
